import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, History, Pill, Upload, UserRound, X } from 'lucide-react';
import {
  applyTerminalPharmacyImport,
  getTerminalPharmacyImportCapability,
  getTerminalPharmacyImportDetail,
  getTerminalPharmacyImportHistory,
  previewTerminalPharmacyImport,
} from '../api/terminalPharmacyImport';
import { getAdminCpList, getAdminCpTerminals } from '../api/cp';

const envEnabled = ['1', 'true', 'yes', 'y'].includes(
  String(process.env.REACT_APP_ADMIN_TERMINAL_PHARMACY_IMPORT_ENABLED || '').trim().toLowerCase(),
);
const envSource = String(process.env.REACT_APP_TERMINAL_PHARMACY_SOURCE || 'DISABLED').trim().toUpperCase();
const pnpDirectImport = ['1', 'true', 'yes', 'y'].includes(
  String(process.env.REACT_APP_ADMIN_SINGLE_CP_MODE || '').trim().toLowerCase(),
);

function toBoolean(value, fallback) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['1', 'true', 'yes', 'y'].includes(normalized)) return true;
    if (['0', 'false', 'no', 'n'].includes(normalized)) return false;
  }
  return fallback;
}

function normalizeCapability(value) {
  const data = value?.terminalPharmacyImport ?? value ?? {};
  return {
    terminalPharmacySource: String(data.terminalPharmacySource ?? data.terminal_pharmacy_source ?? envSource).toUpperCase(),
    excelImportEnabled: toBoolean(data.excelImportEnabled ?? data.excel_import_enabled, envEnabled),
    canExcelImport: toBoolean(data.canExcelImport ?? data.can_excel_import, undefined),
    otpRegistered: toBoolean(data.otpRegistered ?? data.otp_registered, undefined),
    otpActive: toBoolean(data.otpActive ?? data.otp_active, undefined),
    targetCp: data.targetCp ?? data.target_cp ?? null,
  };
}

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString('ko-KR');
}

function formatUploadFileName(value) {
  if (!value) return '-';
  const fileName = String(value);
  if (!/[\u00c2-\u00ff]/.test(fileName)) return fileName;

  try {
    const windows1252Bytes = {
      0x20ac: 0x80, 0x201a: 0x82, 0x0192: 0x83, 0x201e: 0x84, 0x2026: 0x85,
      0x2020: 0x86, 0x2021: 0x87, 0x02c6: 0x88, 0x2030: 0x89, 0x0160: 0x8a,
      0x2039: 0x8b, 0x0152: 0x8c, 0x017d: 0x8e, 0x2018: 0x91, 0x2019: 0x92,
      0x201c: 0x93, 0x201d: 0x94, 0x2022: 0x95, 0x2013: 0x96, 0x2014: 0x97,
      0x02dc: 0x98, 0x2122: 0x99, 0x0161: 0x9a, 0x203a: 0x9b, 0x0153: 0x9c,
      0x017e: 0x9e, 0x0178: 0x9f,
    };
    const bytes = Uint8Array.from(Array.from(fileName), (character) => {
      const code = character.charCodeAt(0);
      const byte = code <= 255 ? code : windows1252Bytes[code];
      if (byte === undefined) throw new Error('Not a Latin-1 mojibake string');
      return byte;
    });
    const decoded = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    return /[가-힣]/.test(decoded) ? decoded : fileName;
  } catch {
    return fileName;
  }
}

function formatManagerLabel(name, id, terminalNames = {}) {
  const managerName = name && name !== '-' ? String(name) : '';
  const managerId = id && id !== '-' ? String(id) : '';
  const numericId = /^\d+$/.test(managerId) ? String(Number(managerId)) : '';
  const resolvedName = managerName || terminalNames[managerId] || (numericId ? terminalNames[numericId] : '') || '';
  if (resolvedName && managerId) return `${resolvedName} (${managerId})`;
  return resolvedName || managerId || '-';
}

const summaryItems = [
  ['newTerminals', '신규 담당자'],
  ['newPharmacies', '신규 거래처'],
  ['updatedPharmacies', '거래처 정보 변경'],
  ['newMappings', '신규 매핑'],
  ['reassignedMappings', '담당자 변경'],
  ['unchangedRows', '변경 없음'],
  ['inactiveRows', '거래중지'],
  ['warnings', '경고'],
];

export default function TerminalPharmacyImport() {
  const [capability, setCapability] = useState(() => normalizeCapability({}));
  const [capabilityLoading, setCapabilityLoading] = useState(true);
  const [capabilityError, setCapabilityError] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [detailRows, setDetailRows] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [otpModal, setOtpModal] = useState(null);
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [working, setWorking] = useState(false);
  const [confirmApply, setConfirmApply] = useState(false);
  const [toast, setToast] = useState('');
  const [terminalNames, setTerminalNames] = useState({});

  const showToast = (message) => {
    setToast(message);
    setTimeout(() => setToast(''), 2500);
  };

  const loadCapability = useCallback(async () => {
    setCapabilityLoading(true);
    setCapabilityError('');
    try {
      if (pnpDirectImport) {
        const data = await getAdminCpList({ page: 1, limit: 1 });
        const rows = data?.rows ?? data?.list ?? (Array.isArray(data) ? data : []);
        const source = Array.isArray(rows) ? rows[0] : null;
        const targetCp = source ? {
          ...source,
          id: source.id ?? source.cpPkId ?? source.cp_pk_id,
          cpId: source.cpId ?? source.cp_id ?? '',
          name: source.name ?? '',
        } : null;
        if (!targetCp?.id) throw new Error('업로드 대상 CP를 찾을 수 없습니다.');
        setCapability(normalizeCapability({
          terminalPharmacySource: 'EXCEL',
          excelImportEnabled: true,
          canExcelImport: true,
          otpRegistered: true,
          otpActive: true,
          targetCp,
        }));
      } else {
        const data = await getTerminalPharmacyImportCapability();
        setCapability(normalizeCapability(data));
      }
    } catch (e) {
      setCapability(normalizeCapability({}));
      setCapabilityError(pnpDirectImport
        ? (e?.message || '업로드 대상 CP 정보를 불러오지 못했습니다.')
        : '관리자 Excel capability API 연결 전입니다. 대상 CP와 OTP 상태가 확인되면 업로드를 사용할 수 있습니다.');
    } finally {
      setCapabilityLoading(false);
    }
  }, []);

  useEffect(() => { loadCapability(); }, [loadCapability]);

  const targetCpId = capability?.targetCp?.id;

  useEffect(() => {
    if (!targetCpId) {
      setTerminalNames({});
      return undefined;
    }
    let cancelled = false;
    getAdminCpTerminals(targetCpId, { page: 1, limit: 500 }).then((data) => {
      if (cancelled) return;
      const rows = data?.rows ?? data?.list ?? data?.terminals ?? (Array.isArray(data) ? data : []);
      const names = {};
      rows.forEach((terminal) => {
        const name = terminal.name ?? terminal.terminalName ?? terminal.terminal_name ?? '';
        const ids = [terminal.terminalId, terminal.terminal_id, terminal.loginId, terminal.login_id, terminal.sourceId, terminal.source_id, terminal.code, terminal.id];
        ids.filter((id) => id !== undefined && id !== null && id !== '').forEach((id) => {
          const key = String(id);
          names[key] = name || names[key] || '';
          if (/^\d+$/.test(key)) names[String(Number(key))] = name || names[String(Number(key))] || '';
        });
      });
      setTerminalNames(names);
    }).catch(() => {
      if (!cancelled) setTerminalNames({});
    });
    return () => { cancelled = true; };
  }, [targetCpId]);

  const loadHistory = useCallback(async () => {
    if (!targetCpId) {
      setHistoryRows([]);
      setHistoryTotal(0);
      return;
    }
    setHistoryLoading(true);
    try {
      const data = await getTerminalPharmacyImportHistory({ cpId: targetCpId, page: historyPage, limit: 20 });
      const rows = Array.isArray(data?.rows) ? data.rows : [];
      setHistoryRows(rows);
      setHistoryTotal(Number(data?.total) || rows.length);
    } catch (e) {
      showToast(e?.message || '업로드 이력을 불러오지 못했습니다.');
    } finally {
      setHistoryLoading(false);
    }
  }, [targetCpId, historyPage]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const openDetail = async (batch) => {
    setSelectedBatch(batch);
    setDetailRows([]);
    setDetailLoading(true);
    try {
      const data = await getTerminalPharmacyImportDetail({ batchId: batch.id, page: 1, limit: 100 });
      setDetailRows(Array.isArray(data?.rows) ? data.rows : []);
    } catch (e) {
      showToast(e?.message || '상세 이력을 불러오지 못했습니다.');
    } finally {
      setDetailLoading(false);
    }
  };

  const otpUnavailable = !pnpDirectImport && (capability.otpRegistered === false || capability.otpActive === false);
  const permissionDenied = capability.canExcelImport === false;
  const featureReady = capability.excelImportEnabled === true &&
    capability.terminalPharmacySource === 'EXCEL' &&
    Boolean(targetCpId) &&
    !otpUnavailable &&
    !permissionDenied;

  const selectFile = (event) => {
    const next = event.target.files?.[0] || null;
    if (next && !/\.xls$/i.test(next.name)) {
      event.target.value = '';
      setFile(null);
      showToast('.xls 파일만 업로드할 수 있습니다. .xlsx는 지원하지 않습니다.');
      return;
    }
    setFile(next);
    setPreview(null);
  };

  const executeImport = async (type, otpToken) => {
    setWorking(true);
    setOtpError('');
    try {
      if (type === 'preview') {
        const data = await previewTerminalPharmacyImport({ cpId: targetCpId, file, otpToken });
        setPreview(data);
        showToast('미리보기가 생성되었습니다. 아직 적용되지 않았습니다.');
      } else if (type === 'apply') {
        const appliedBatch = { id: preview.batchId, file_name: file?.name || 'Excel 업로드' };
        await applyTerminalPharmacyImport({ batchId: appliedBatch.id, otpToken });
        showToast('Excel 변경사항이 적용되었습니다.');
        await loadHistory();
        await openDetail(appliedBatch);
        setPreview(null);
      }
      setOtpModal(null);
      setOtp('');
    } catch (e) {
      const message = e?.message || (pnpDirectImport ? '요청 처리에 실패했습니다.' : 'OTP 인증 또는 요청 처리에 실패했습니다.');
      if (pnpDirectImport) showToast(message);
      else setOtpError(message);
    } finally {
      setOtp('');
      setWorking(false);
    }
  };

  const requestPreview = () => {
    if (!featureReady || !file) return;
    if (pnpDirectImport) {
      executeImport('preview');
      return;
    }
    setOtp('');
    setOtpError('');
    setOtpModal({ type: 'preview' });
  };

  const requestApply = () => {
    if (!preview?.batchId || !featureReady) return;
    setConfirmApply(true);
  };

  const proceedApplyOtp = () => {
    setConfirmApply(false);
    if (pnpDirectImport) {
      executeImport('apply');
      return;
    }
    setOtp('');
    setOtpError('');
    setOtpModal({ type: 'apply' });
  };

  const submitOtpAction = () => {
    if (!/^\d{6}$/.test(otp)) {
      setOtpError('현재 OTP 6자리를 입력해 주세요.');
      return;
    }
    executeImport(otpModal?.type, otp);
  };

  const summary = preview?.summary || {};
  const previewRows = Array.isArray(preview?.rows) ? preview.rows : [];
  const lastHistoryPage = Math.max(1, Math.ceil(historyTotal / 20));

  return (
    <div className="flex-1 h-full min-h-0 overflow-y-auto bg-[#f7f8fa] p-4 md:p-6">
      {toast && <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[4000] rounded-xl bg-[#191f28] text-white px-4 py-3 shadow-xl text-sm">{toast}</div>}
      <div className="max-w-[1500px] mx-auto space-y-5">
        <header>
          <h1 className="text-2xl font-bold text-[#191f28]">담당자·약국 Excel 관리</h1>
          <p className="mt-1 text-sm text-[#6b7684]">담당자·거래처·매핑 변경사항을 미리 확인한 후 적용합니다.</p>
        </header>

        {capabilityError && <Notice tone="warning">{capabilityError}</Notice>}
        {permissionDenied && <Notice tone="error">SUPER 관리자만 Excel 업로드를 사용할 수 있습니다.</Notice>}
        {otpUnavailable && (
          <Notice tone="warning">
            관리자 OTP 기기가 등록·활성화되지 않았습니다. 관리자 로그인 OTP 등록 화면에서 Google Authenticator를 먼저 연결해 주세요.
          </Notice>
        )}

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 lg:items-stretch">
          <section className="min-w-0 rounded-2xl bg-white border border-[#e8ebed] p-5 shadow-sm flex flex-col">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div>
                <h2 className="font-bold text-lg text-[#191f28] flex items-center gap-2"><Upload size={20} /> Excel 업로드</h2>
                <p className="text-sm text-[#6b7684] mt-1">
                  대상 CP: {capability.targetCp ? capability.targetCp.name + ' (' + capability.targetCp.cpId + ')' : '대상 CP 확인 필요'}
                </p>
              </div>
              <button type="button" onClick={loadCapability} disabled={capabilityLoading} className="px-3 py-2 rounded-lg border border-[#dfe3e6] text-sm">
                {capabilityLoading ? '확인 중...' : (pnpDirectImport ? '대상 CP 다시 확인' : '설정 다시 확인')}
              </button>
            </div>

            <div className="flex-1 rounded-xl bg-[#f8faf9] border border-[#e5ebe7] p-4">
              <label className="block text-sm font-bold text-[#333d4b] mb-2">.xls 파일 선택</label>
              <input className="block max-w-full text-sm" type="file" accept=".xls,application/vnd.ms-excel" onChange={selectFile} />
              <p className="text-xs text-[#8b95a1] mt-3 leading-5">필수 헤더: 담당자 | 담당자ID | 거래처코드 | 거래처명 | 사업자등록번호 | 대표자명 | 연락처</p>
            </div>
            <button
              type="button"
              onClick={requestPreview}
              disabled={!featureReady || !file || working}
              className="mt-4 self-start px-5 py-3 rounded-xl bg-[var(--brand-primary)] text-white font-bold disabled:opacity-40"
            >
              {working ? '처리 중...' : (pnpDirectImport ? '변경사항 미리보기' : 'OTP 인증 후 미리보기')}
            </button>
          </section>

          <section className="min-w-0 rounded-2xl bg-white border border-[#e8ebed] p-5 shadow-sm flex flex-col">
            <div className="mb-3 flex items-center justify-between gap-3">
              <h2 className="font-bold text-lg text-[#191f28] flex items-center gap-2"><History size={20} /> 업로드 이력</h2>
              <span className="text-xs font-semibold text-[#6b7684]">총 {historyTotal.toLocaleString()}건</span>
            </div>
            {historyLoading ? <p className="flex-1 py-10 text-center text-[#8b95a1]">이력을 불러오는 중입니다.</p> : (
              <div className="max-h-[250px] flex-1 overflow-auto rounded-xl border border-[#eef1f4]">
                <table className="w-full min-w-[620px] text-sm">
                  <thead className="sticky top-0 z-10 bg-[#fafbfb]"><tr className="text-left text-[#6b7684] border-b">
                    <th className="p-3">Batch</th><th className="p-3">파일</th><th className="p-3">상태</th><th className="p-3 text-right">총 행</th><th className="p-3">생성일</th>
                  </tr></thead>
                  <tbody>
                    {historyRows.map((row) => {
                      const fileName = formatUploadFileName(row.file_name ?? row.fileName);
                      return (
                        <tr key={row.id ?? row.batchId} onClick={() => openDetail(row)} className="border-b last:border-0 hover:bg-[#f8faf9] cursor-pointer">
                          <td className="p-3 font-bold">#{row.id ?? row.batchId}</td>
                          <td className="max-w-[220px] truncate p-3" title={fileName}>{fileName}</td>
                          <td className="p-3">{row.status ?? '-'}</td>
                          <td className="p-3 text-right tabular-nums">{Number(row.total_rows ?? row.totalRows ?? 0).toLocaleString()}</td>
                          <td className="whitespace-nowrap p-3">{formatDate(row.created_at ?? row.createdAt)}</td>
                        </tr>
                      );
                    })}
                    {historyRows.length === 0 && <tr><td colSpan="5" className="p-10 text-center text-[#8b95a1]">업로드 이력이 없습니다.</td></tr>}
                  </tbody>
                </table>
              </div>
            )}
            {historyTotal > 20 && <div className="flex items-center justify-center gap-4 mt-4">
              <button disabled={historyPage <= 1} onClick={() => setHistoryPage((v) => v - 1)} className="px-3 py-2 border rounded-lg disabled:opacity-40">이전</button>
              <span className="text-sm">{historyPage} / {lastHistoryPage}</span>
              <button disabled={historyPage >= lastHistoryPage} onClick={() => setHistoryPage((v) => v + 1)} className="px-3 py-2 border rounded-lg disabled:opacity-40">다음</button>
            </div>}
          </section>
        </div>

        {preview && (
          <section className="rounded-2xl bg-white border border-[#e8ebed] p-5 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div>
                <h2 className="font-bold text-lg text-[#191f28]">변경사항 · Batch #{preview.batchId}</h2>
                <p className="text-sm text-[#6b7684] mt-1">총 {preview.totalRows ?? 0}행 · 미리보기만 생성되었으며 아직 적용되지 않았습니다.</p>
              </div>
              <button type="button" onClick={requestApply} disabled={!featureReady || working} className="px-5 py-3 rounded-xl bg-[#191f28] text-white font-bold disabled:opacity-40">최종 적용</button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3 mb-5">
              {summaryItems.map(([key, label]) => (
                <div key={key} className={'rounded-xl border p-3 ' + (key === 'reassignedMappings' && Number(summary[key]) > 0 ? 'border-[#f59e0b] bg-[#fff8e6]' : 'border-[#e8ebed] bg-[#fafbfb]')}>
                  <span className="block text-xs text-[#6b7684]">{label}</span>
                  <strong className="block text-xl mt-1">{Number(summary[key] || 0).toLocaleString()}</strong>
                </div>
              ))}
            </div>
            <ImportChangePreview rows={previewRows} terminalNames={terminalNames} emptyText="응답에 상세 행이 없습니다. 업로드 이력의 상세 화면에서 확인할 수 있습니다." />
          </section>
        )}
      </div>

      {otpModal && <OtpModal otp={otp} setOtp={setOtp} error={otpError} working={working} title={otpModal.type === 'preview' ? 'Excel 미리보기 OTP' : 'Excel 적용 OTP'} onClose={() => { if (!working) { setOtp(''); setOtpModal(null); } }} onSubmit={submitOtpAction} />}
      {confirmApply && <ConfirmApply summary={summary} direct={pnpDirectImport} onCancel={() => setConfirmApply(false)} onConfirm={proceedApplyOtp} />}
      {selectedBatch && <DetailModal batch={selectedBatch} rows={detailRows} terminalNames={terminalNames} loading={detailLoading} onClose={() => setSelectedBatch(null)} />}
    </div>
  );
}

function Notice({ children, tone }) {
  const error = tone === 'error';
  return <div className={'rounded-xl border px-4 py-3 flex gap-2 text-sm ' + (error ? 'border-red-200 bg-red-50 text-red-700' : 'border-amber-200 bg-amber-50 text-amber-800')}><AlertTriangle size={18} className="shrink-0" />{children}</div>;
}

const actionLabels = {
  CREATE_TERMINAL: '담당자 추가', ADD_TERMINAL: '담당자 추가', NEW_TERMINAL: '담당자 추가',
  CREATE_MANAGER: '담당자 추가', ADD_MANAGER: '담당자 추가', NEW_MANAGER: '담당자 추가',
  CREATE_SALESPERSON: '담당자 추가', ADD_SALESPERSON: '담당자 추가', CREATE_DRIVER: '담당자 추가', ADD_DRIVER: '담당자 추가',
  UPDATE_TERMINAL: '담당자 정보 변경', CHANGE_TERMINAL: '담당자 정보 변경', MODIFY_TERMINAL: '담당자 정보 변경',
  UPDATE_MANAGER: '담당자 정보 변경', CHANGE_MANAGER: '담당자 정보 변경', MODIFY_MANAGER: '담당자 정보 변경',
  UPDATE_SALESPERSON: '담당자 정보 변경', CHANGE_SALESPERSON: '담당자 정보 변경', UPDATE_DRIVER: '담당자 정보 변경', CHANGE_DRIVER: '담당자 정보 변경',
  CREATE_PHARMACY: '약국 추가', ADD_PHARMACY: '약국 추가', NEW_PHARMACY: '약국 추가', CREATE_CUSTOMER: '약국 추가',
  UPDATE_PHARMACY: '약국 정보 변경', CHANGE_PHARMACY: '약국 정보 변경', MODIFY_PHARMACY: '약국 정보 변경', UPDATE_CUSTOMER: '약국 정보 변경',
  CREATE_MAPPING: '담당 연결', REASSIGN_MAPPING: '담당자 변경', CHANGE_MAPPING: '담당자 변경',
  INACTIVATE_MAPPING: '거래중지', DEACTIVATE_MAPPING: '거래중지',
};

function getActions(row) {
  const raw = row.planned_actions ?? row.plannedActions ?? row.actions ?? [];
  const values = Array.isArray(raw) ? raw : String(raw).split(/[,|]/);
  return values.map((value) => String(value?.type ?? value?.action ?? value?.code ?? value).trim().toUpperCase()).filter(Boolean);
}

function firstPresent(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== '');
}

function asObject(value) {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value;
  if (typeof value === 'string' && value.trim().startsWith('{')) {
    try { return JSON.parse(value); } catch (_) { return {}; }
  }
  return {};
}

function textValue(value) {
  if (value === undefined || value === null || value === '') return '';
  if (Array.isArray(value)) return value.map(textValue).filter(Boolean).join(', ');
  if (typeof value !== 'object') return String(value);
  const before = firstPresent(value.before, value.old, value.oldValue, value.old_value, value.from);
  const after = firstPresent(value.after, value.new, value.newValue, value.new_value, value.to);
  if (before !== undefined || after !== undefined) return `${textValue(before) || '-'} → ${textValue(after) || '-'}`;
  return Object.values(value).map(textValue).filter(Boolean).join(', ');
}

const changeFieldLabels = {
  customerName: '약국명', customer_name: '약국명', pharmacyName: '약국명', pharmacy_name: '약국명',
  businessNo: '사업자번호', business_no: '사업자번호', representativeName: '대표자명', representative_name: '대표자명',
  phone: '연락처', status: '상태', managerName: '담당자', manager_name: '담당자', terminalName: '담당자', terminal_name: '담당자',
};

function changeDetailText(row) {
  const raw = firstPresent(row.changeDetails, row.change_details, row.changeSummary, row.change_summary, row.changedFields, row.changed_fields, row.fieldChanges, row.field_changes, row.diffDetails, row.diff_details, row.diffs, row.diff, row.changes);
  if (!raw) return '';
  if (typeof raw === 'string') return raw;
  const entries = Array.isArray(raw) ? raw.map((value, index) => [index, value]) : Object.entries(raw);
  return entries.map(([key, value]) => {
    if (typeof value === 'string') return value;
    const item = asObject(value);
    const field = firstPresent(item.label, item.fieldLabel, item.field_label, item.field, item.key, key);
    return `${changeFieldLabels[field] || field}: ${textValue(value)}`;
  }).filter(Boolean).join(', ');
}

function normalizePreviewRow(row, index) {
  const source = asObject(firstPresent(row.rowData, row.row_data, row.rawData, row.raw_data, row.excelRow, row.excel_row, row.payload));
  const manager = asObject(firstPresent(row.manager, row.terminal, row.salesperson, row.newManager, row.new_manager, row.newTerminal, row.new_terminal, source.manager, source.terminal));
  const customer = asObject(firstPresent(row.customer, row.pharmacy, row.newCustomer, row.new_customer, row.newPharmacy, row.new_pharmacy, source.customer, source.pharmacy));
  const previousManager = asObject(firstPresent(row.previousManager, row.previous_manager, row.previousTerminal, row.previous_terminal, row.oldManager, row.old_manager, row.existingManager, row.existing_manager));
  const actions = getActions(row);
  return {
    raw: row,
    key: firstPresent(row.id, row.rowNo, row.row_no, source.rowNo, source.row_no, index),
    actions: actions.length ? actions : getActions(source),
    managerName: textValue(firstPresent(row.managerName, row.manager_name, row.terminalName, row.terminal_name, row.salespersonName, row.salesperson_name, source.managerName, source.manager_name, source.terminalName, source.terminal_name, typeof row.manager === 'string' ? row.manager : undefined, manager.name, manager.managerName, manager.manager_name, manager.terminalName, manager.terminal_name)) || '-',
    managerSourceId: textValue(firstPresent(row.managerSourceId, row.manager_source_id, row.managerId, row.manager_id, row.terminalId, row.terminal_id, row.terminalLoginId, row.terminal_login_id, row.loginId, row.login_id, row.sourceId, row.source_id, source.managerSourceId, source.manager_source_id, source.managerId, source.manager_id, source.terminalId, source.terminal_id, source.loginId, source.login_id, manager.sourceId, manager.source_id, manager.managerId, manager.manager_id, manager.terminalId, manager.terminal_id, manager.loginId, manager.login_id, manager.id)) || '-',
    customerCode: textValue(firstPresent(row.customerCode, row.customer_code, row.externalCustomerCode, row.external_customer_code, row.institutionCode, row.institution_code, row.pharmacyCode, row.pharmacy_code, source.customerCode, source.customer_code, source.externalCustomerCode, source.external_customer_code, source.institutionCode, source.institution_code, customer.code, customer.customerCode, customer.customer_code, customer.externalCustomerCode, customer.external_customer_code, customer.institutionCode, customer.institution_code)) || '-',
    customerName: textValue(firstPresent(row.customerName, row.customer_name, row.pharmacyName, row.pharmacy_name, source.customerName, source.customer_name, source.pharmacyName, source.pharmacy_name, typeof row.customer === 'string' ? row.customer : undefined, customer.name, customer.customerName, customer.customer_name, customer.pharmacyName, customer.pharmacy_name)) || '-',
    previousManagerName: textValue(firstPresent(row.previousManagerName, row.previous_manager_name, row.previousTerminalName, row.previous_terminal_name, row.oldManagerName, row.old_manager_name, previousManager.name, previousManager.managerName, previousManager.manager_name, previousManager.terminalName, previousManager.terminal_name)) || '-',
    previousManagerSourceId: textValue(firstPresent(row.previousManagerSourceId, row.previous_manager_source_id, row.previousTerminalId, row.previous_terminal_id, row.oldManagerId, row.old_manager_id, previousManager.sourceId, previousManager.source_id, previousManager.managerId, previousManager.manager_id, previousManager.terminalId, previousManager.terminal_id, previousManager.loginId, previousManager.login_id, previousManager.id)) || '-',
    message: textValue(firstPresent(row.warningMessage, row.warning_message, row.warnings, row.applyResult, row.apply_result, row.resultMessage, row.result_message)),
    changeDetail: changeDetailText(row),
  };
}

function isAddAction(action, subject) {
  return new RegExp(`(?:CREATE|ADD|NEW)_${subject}|${subject}_(?:CREATE|ADD|NEW)`).test(action);
}

function isChangeAction(action, subject) {
  return new RegExp(`(?:UPDATE|CHANGE|MODIFY)_${subject}|${subject}_(?:UPDATE|CHANGE|MODIFY)`).test(action);
}

function actionText(actions) {
  return [...new Set(actions.map((action) => actionLabels[action] || action.replaceAll('_', ' ')))].join(', ') || '-';
}

function ChangeBadge({ type }) {
  const added = type === '추가';
  return <span className={'inline-flex rounded-full px-2.5 py-1 text-xs font-bold ' + (added ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700')}>{type}</span>;
}

function SectionCount({ label, count, tone }) {
  const color = tone === 'add' ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700';
  return <span className={'rounded-full px-2.5 py-1 text-xs font-bold ' + color}>{label} {count.toLocaleString()}건</span>;
}

function ImportChangePreview({ rows, emptyText, terminalNames = {} }) {
  if (!rows.length) return <p className="text-center text-sm text-[#8b95a1] py-8">{emptyText}</p>;

  const terminalMap = new Map();
  const pharmacyChanges = [];

  rows.forEach((row, index) => {
    const normalized = normalizePreviewRow(row, index);
    const actions = normalized.actions;
    const terminalSubject = '(?:TERMINAL|MANAGER|SALESPERSON|DRIVER)';
    const terminalAdd = actions.some((action) => isAddAction(action, terminalSubject));
    const terminalChange = actions.some((action) => isChangeAction(action, terminalSubject));
    const pharmacyAdd = actions.some((action) => isAddAction(action, '(?:PHARMACY|CUSTOMER)'));
    const pharmacyChange = actions.some((action) => (
      isChangeAction(action, '(?:PHARMACY|CUSTOMER)') ||
      action.includes('MAPPING') ||
      action.includes('INACTIVE') ||
      action.includes('STOP')
    ));

    if (terminalAdd || terminalChange) {
      const key = normalized.managerSourceId !== '-' ? normalized.managerSourceId : `${normalized.managerName}-${index}`;
      const current = terminalMap.get(key) || {
        key,
        name: normalized.managerName,
        sourceId: normalized.managerSourceId,
        type: terminalAdd ? '추가' : '변경',
        actions: [],
        pharmacyCodes: new Set(),
        message: normalized.message,
        changeDetail: normalized.changeDetail,
      };
      if (terminalAdd) current.type = '추가';
      current.actions.push(...actions.filter((action) => /TERMINAL|MANAGER|SALESPERSON|DRIVER/.test(action)));
      if (normalized.customerCode !== '-') current.pharmacyCodes.add(normalized.customerCode);
      if (current.name === '-' && normalized.managerName !== '-') current.name = normalized.managerName;
      if (current.sourceId === '-' && normalized.managerSourceId !== '-') current.sourceId = normalized.managerSourceId;
      if (!current.message && normalized.message) current.message = normalized.message;
      if (!current.changeDetail && normalized.changeDetail) current.changeDetail = normalized.changeDetail;
      terminalMap.set(key, current);
    }

    if (pharmacyAdd || pharmacyChange) {
      pharmacyChanges.push({
        ...normalized,
        type: pharmacyAdd ? '추가' : '변경',
      });
    }
  });

  const terminalChanges = [...terminalMap.values()];
  const terminalAdded = terminalChanges.filter((row) => row.type === '추가').length;
  const terminalUpdated = terminalChanges.length - terminalAdded;
  const pharmacyAdded = pharmacyChanges.filter((row) => row.type === '추가').length;
  const pharmacyUpdated = pharmacyChanges.length - pharmacyAdded;

  return <div className="space-y-4">
    <section className="overflow-hidden rounded-xl border border-[#e5e8eb]">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#e5e8eb] bg-[#fafbfb] px-4 py-3">
        <h3 className="flex items-center gap-2 font-bold text-[#191f28]"><UserRound size={18} /> 담당자 변경 내역</h3>
        <div className="flex gap-2"><SectionCount label="추가" count={terminalAdded} tone="add" /><SectionCount label="변경" count={terminalUpdated} /></div>
      </div>
      <div className="max-h-[260px] overflow-auto">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="sticky top-0 bg-white text-left text-[#6b7684]"><tr className="border-b"><th className="p-3 w-[90px]">구분</th><th className="p-3">담당자</th><th className="p-3">담당자 ID</th><th className="p-3">관련 약국</th><th className="p-3">변경 내용</th><th className="p-3">경고/결과</th></tr></thead>
          <tbody>{terminalChanges.map((row) => <tr key={row.key} className="border-b last:border-0">
            <td className="p-3"><ChangeBadge type={row.type} /></td><td className="p-3 font-semibold text-[#191f28]">{row.name}</td><td className="p-3 font-mono">{row.sourceId}</td><td className="p-3">{row.pharmacyCodes.size.toLocaleString()}곳</td><td className="p-3 break-words">{row.changeDetail || actionText(row.actions)}</td><td className="p-3 text-[#6b7684]">{row.message || '-'}</td>
          </tr>)}</tbody>
        </table>
        {terminalChanges.length === 0 && <p className="py-8 text-center text-sm text-[#8b95a1]">추가·변경될 담당자가 없습니다.</p>}
      </div>
    </section>

    <section className="overflow-hidden rounded-xl border border-[#e5e8eb]">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#e5e8eb] bg-[#fafbfb] px-4 py-3">
        <h3 className="flex items-center gap-2 font-bold text-[#191f28]"><Pill size={18} /> 약국 변경 내역</h3>
        <div className="flex gap-2"><SectionCount label="추가" count={pharmacyAdded} tone="add" /><SectionCount label="변경" count={pharmacyUpdated} /></div>
      </div>
      <div className="max-h-[360px] overflow-auto">
        <table className="w-full min-w-[980px] text-sm">
          <thead className="sticky top-0 bg-white text-left text-[#6b7684]"><tr className="border-b"><th className="p-3 w-[90px]">구분</th><th className="p-3">거래처코드</th><th className="p-3">약국명</th><th className="p-3">담당자</th><th className="p-3">이전 담당자</th><th className="p-3">변경 내용</th><th className="p-3">경고/결과</th></tr></thead>
          <tbody>{pharmacyChanges.map((row) => {
            const reassigned = row.actions.some((action) => action.includes('REASSIGN') || action === 'CHANGE_MANAGER' || action === 'CHANGE_TERMINAL');
            return <tr key={row.key} className={'border-b last:border-0 ' + (reassigned ? 'bg-amber-50/60' : '')}>
              <td className="p-3"><ChangeBadge type={row.type} /></td><td className="p-3 font-mono">{row.customerCode}</td><td className="p-3 font-semibold text-[#191f28]">{row.customerName}</td><td className="p-3">{row.managerName !== '-' ? row.managerName : row.managerSourceId}</td><td className="p-3">{reassigned ? formatManagerLabel(row.previousManagerName, row.previousManagerSourceId, terminalNames) : '-'}</td><td className="p-3 break-words">{row.changeDetail || actionText(row.actions.filter((action) => !/TERMINAL|MANAGER|SALESPERSON|DRIVER/.test(action)))}</td><td className="p-3 text-[#6b7684]">{row.message || '-'}</td>
            </tr>;
          })}</tbody>
        </table>
        {pharmacyChanges.length === 0 && <p className="py-8 text-center text-sm text-[#8b95a1]">추가·변경될 약국이 없습니다.</p>}
      </div>
    </section>
  </div>;
}

function OtpModal({ otp, setOtp, error, working, title, onClose, onSubmit }) {
  return <Modal onClose={onClose}>
    <h2 className="text-lg font-bold mb-2">{title}</h2>
    <p className="text-sm text-[#6b7684] mb-4">Google Authenticator에 표시되는 현재 6자리 숫자를 입력하세요.</p>
    <input autoFocus value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} onKeyDown={(e) => { if (e.key === 'Enter') onSubmit(); }} inputMode="numeric" maxLength={6} placeholder="000000" className="w-full border rounded-xl px-4 py-3 text-center text-2xl tracking-[0.45em]" />
    {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
    <div className="flex gap-2 mt-5"><button disabled={working} onClick={onClose} className="flex-1 border rounded-xl py-3">취소</button><button disabled={working || otp.length !== 6} onClick={onSubmit} className="flex-1 rounded-xl py-3 bg-[#191f28] text-white disabled:opacity-40">{working ? '처리 중...' : '확인'}</button></div>
  </Modal>;
}

function ConfirmApply({ summary, direct, onCancel, onConfirm }) {
  return <Modal onClose={onCancel}>
    <h2 className="text-lg font-bold mb-3">Excel 변경사항 적용</h2>
    <p className="text-sm text-[#4e5968] leading-6">신규 담당자 {Number(summary.newTerminals || 0)}명, 신규 약국 {Number(summary.newPharmacies || 0)}건, 담당자 변경 {Number(summary.reassignedMappings || 0)}건을 적용합니다.<br />사라진 담당자와 약국은 삭제되지 않습니다. 계속하시겠습니까?</p>
    <div className="flex gap-2 mt-5"><button onClick={onCancel} className="flex-1 border rounded-xl py-3">취소</button><button onClick={onConfirm} className="flex-1 rounded-xl py-3 bg-[#191f28] text-white">{direct ? '변경사항 적용' : 'OTP 입력 후 적용'}</button></div>
  </Modal>;
}

function DetailModal({ batch, rows, terminalNames, loading, onClose }) {
  return <Modal onClose={onClose} wide>
    <div className="flex items-center justify-between gap-4 mb-4"><div><h2 className="text-lg font-bold">Batch #{batch.id} 상세</h2><p className="text-sm text-[#6b7684]">{formatUploadFileName(batch.file_name ?? batch.fileName)}</p></div><button onClick={onClose}><X size={22} /></button></div>
    {loading ? <p className="py-10 text-center text-[#8b95a1]">상세를 불러오는 중입니다.</p> : <ImportChangePreview rows={rows} terminalNames={terminalNames} emptyText="추가·변경된 상세 행이 없습니다." />}
  </Modal>;
}

function Modal({ children, onClose, wide = false }) {
  return <div onClick={onClose} className="fixed inset-0 z-[3500] bg-black/45 flex items-center justify-center p-4"><div onClick={(e) => e.stopPropagation()} className={'bg-white rounded-2xl shadow-2xl p-5 max-h-[90vh] overflow-auto w-full ' + (wide ? 'max-w-[1250px]' : 'max-w-md')}>{children}</div></div>;
}
